import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Grid from '@mui/material/Grid2'
import ArrowIcon from '@mui/icons-material/ArrowForwardIos'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PollIcon from '@mui/icons-material/Poll'
import SearchIcon from '@mui/icons-material/Search'
import BusinessIcon from '@mui/icons-material/Business'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SettingsIcon from '@mui/icons-material/Settings'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import TeamIcon from '@mui/icons-material/People'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded'
import HelpIcon from '@mui/icons-material/Help'
import ChevronIcon from '@mui/icons-material/DoubleArrowSharp'
import DiamondIcon from '@mui/icons-material/Diamond'
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket'
import ChatIcon from '@mui/icons-material/Chat'
import LogoDevIcon from '@mui/icons-material/LogoDev'
import WorkIcon from '@mui/icons-material/Work'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import logoWhite from 'assets/images/caleoLogo.svg'
import { useLogin, useUser } from 'hooks'
import { useMenu } from './menu'
import caleoFlame from 'assets/images/Caleo-flame.png'
import StatisticsIcon from '@mui/icons-material/PieChart'
import { INetwork } from 'types/networkInterfaces'
import { networkAPI } from 'api/NetworkAPI'
import ErrorOverlay from '../ErrorOverlay'
import { IError } from 'types/error'
import MenuIcon from '@mui/icons-material/Menu'
import { ToolbarItems } from '../Toolbar/ToolbarItems'

/**
 * Side navigation menu.
 *
 * @returns Navigation menu component.
 * @notExported
 */
const NavigationMenu: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { groups, networkAccess, user, ready, features } = useUser()
  const { loggedIn } = useLogin()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))
  const { showMenu, setMenu } = useMenu()
  const [open, setOpen] = useState<boolean>(false)
  const [networks, setNetworks] = useState<INetwork[]>()
  const [backendError, setBackendError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (user) {
        try {
          const results = await networkAPI.getNetworkBenefits(controller)
          setNetworks(results)
        } catch (error) {
          setBackendError(error as IError)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [user])

  useEffect(() => {
    document.title = 'Caleo | ' + t(`navigation.${location.pathname.split('/')[1]}`)
  }, [location.pathname, t])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  let hideNavMenu = true
  if (!user || !loggedIn) {
    hideNavMenu = true
  } else {
    if (location.pathname.includes('/assignments/public') || location.pathname.includes('/workorders/public')) {
      hideNavMenu = true
    } else {
      hideNavMenu = false
    }
  }

  const person = ready && user && user.Person !== null

  const listItem = (id: string, action: string, icon: React.ReactElement, text: string, currentPage: boolean) => {
    return (
      <ListItem disablePadding>
        <ListItemButton
          id={id}
          selected={currentPage}
          sx={{
            paddingLeft: '23px',
            paddingRight: '0px',
            [theme.breakpoints.down('sm')]: {
              paddingLeft: '13px',
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.secondary.main}`,
              outlineOffset: -2,
            },
          }}
          onClick={() => {
            navigate(action)
          }}
        >
          {showMenu ? (
            <>
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={text} />
              {currentPage && (
                <ListItemIcon>
                  <ArrowIcon />
                </ListItemIcon>
              )}
            </>
          ) : (
            <Tooltip title={String(text)} placement="right" arrow>
              <ListItemIcon>{icon}</ListItemIcon>
            </Tooltip>
          )}
        </ListItemButton>
      </ListItem>
    )
  }

  const handleLogoClick = () => {
    if (groups.includes('sales')) {
      navigate('/employees')
    } else {
      navigate('/profile')
    }
  }

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  return (
    <>
      {!hideNavMenu && (
        <>
          <ToolbarItems>
            <IconButton
              data-testid="button-nav-open-drawer-mobile"
              color="primary"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                mr: 2,
                display: { sx: 'block', md: 'none' },
                [theme.breakpoints.up('sm')]: {
                  visibility: 'hidden',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </ToolbarItems>
          <Drawer
            variant={!mobileView || (mobileView && open) ? 'permanent' : 'persistent'}
            open={showMenu}
            sx={{
              ...{ width: '240px', flexShrink: 0, zIndex: 1299, whiteSpace: 'nowrap' },
              ...(showMenu
                ? {
                    backgroundColor: theme.palette.primary.main,
                    width: '240px',
                    transition: theme.transitions.create('width', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.enteringScreen,
                    }),
                  }
                : {
                    backgroundColor: theme.palette.primary.main,
                    transition: theme.transitions.create('width', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                    }),
                    overflowX: 'hidden',
                    width: theme.spacing(7) + 1,
                    [theme.breakpoints.up('sm')]: {
                      width: theme.spacing(9) + 1,
                    },
                  }),
            }}
            slotProps={{
              paper: {
                sx: showMenu
                  ? {
                      backgroundColor: theme.palette.primary.main,
                      width: '240px',
                      transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                    }
                  : {
                      backgroundColor: theme.palette.primary.main,
                      transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                      overflowX: 'hidden',
                      width: theme.spacing(7) + 1,
                      [theme.breakpoints.up('sm')]: {
                        width: theme.spacing(9) + 1,
                      },
                    },
              },
            }}
          >
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: theme.spacing(0, 1),
                ...theme.mixins.toolbar,
                justifyContent: 'flex-end',
              }}
            >
              {showMenu ? (
                <>
                  <Box
                    component="button"
                    type="button"
                    aria-label="Go to homepage"
                    onClick={handleLogoClick}
                    sx={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      cursor: 'pointer',
                      width: '74%',
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.secondary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box component="img" src={logoWhite} alt="" sx={{ width: '100%', p: '15px' }} />
                  </Box>
                  <IconButton
                    data-testid="button-nav-close-drawer-desktop"
                    sx={{
                      color: theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: 'unset !important',
                      },
                    }}
                    aria-label="close drawer"
                    onClick={() => setMenu(false)}
                    size="large"
                  >
                    <ChevronIcon style={{ transform: 'rotate(-180deg)' }} />
                  </IconButton>
                </>
              ) : open ? (
                <Grid container direction="column" justifyContent="center" alignItems="center">
                  <Grid>
                    <IconButton
                      data-testid="button-nav-close-drawer-mobile"
                      aria-label="close drawer"
                      color="secondary"
                      onClick={handleDrawerClose}
                      edge="start"
                      sx={{ ml: '2px' }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ) : (
                <Grid container direction="column" justifyContent="center" alignItems="center">
                  <Grid>
                    <Box
                      component="button"
                      type="button"
                      aria-label="Go to homepage"
                      onClick={handleLogoClick}
                      sx={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        cursor: 'pointer',
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.secondary.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={caleoFlame}
                        alt=""
                        sx={{
                          width: '44px',
                          mt: '10px',
                          ml: '6px',
                          mr: '6px',
                          [theme.breakpoints.down('sm')]: {
                            width: '26px',
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid>
                    <IconButton
                      data-testid="button-nav-open-drawer-desktop"
                      sx={{
                        color: theme.palette.secondary.main,
                        '&:hover': {
                          backgroundColor: 'unset !important',
                        },
                      }}
                      aria-label="open drawer"
                      onClick={() => setMenu(true)}
                      size="large"
                    >
                      <ChevronIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Divider />
            <nav aria-label="primary navigation">
              <List>
                {features &&
                  features.includes('profile') &&
                  listItem(
                    'nav-user',
                    '/profile',
                    <PersonOutlineIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/profile')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.profile'),
                    location.pathname.includes('/profile')
                  )}
                {features &&
                  groups &&
                  features.includes('profile') &&
                  !(groups.includes('sales') || groups.includes('companyAdmin') || groups.includes('admin')) &&
                  person &&
                  listItem(
                    'nav-print',
                    '/print',
                    <PictureAsPdf
                      sx={{
                        backgroundColor: location.pathname.includes('/print')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.print'),
                    location.pathname.includes('/print')
                  )}
                {features &&
                  features.includes('allocations') &&
                  person &&
                  listItem(
                    'nav-allocation',
                    '/allocation',
                    <PollIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/allocation')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.allocation'),
                    location.pathname.includes('/allocation')
                  )}
                {features &&
                  groups &&
                  features.includes('search') &&
                  (groups.includes('admin') || groups.includes('sales')) &&
                  person &&
                  listItem(
                    'nav-search',
                    '/employees',
                    <SearchIcon
                      sx={{
                        backgroundColor:
                          location.pathname.includes('/employees') &&
                          !location.pathname.includes('/company-admin') &&
                          !location.pathname.includes('/networks') &&
                          !location.pathname.includes('/admin')
                            ? theme.palette.secondary.main
                            : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.employees'),
                    location.pathname.includes('/employees') &&
                      !location.pathname.includes('/company-admin') &&
                      !location.pathname.includes('/networks') &&
                      !location.pathname.includes('/admin')
                  )}
                {features &&
                  groups &&
                  features.includes('assignments') &&
                  (groups.includes('sales') || groups.includes('freelancer')) &&
                  listItem(
                    'nav-assignments',
                    '/assignments',
                    <AssignmentIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/assignments')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.assignments'),
                    location.pathname.includes('/assignments')
                  )}
                {features &&
                  groups &&
                  features.includes('workOrders') &&
                  (groups.includes('sales') || groups.includes('freelancer')) &&
                  listItem(
                    'nav-workorders',
                    '/workorders',
                    <WorkIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/workorders')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.workorders'),
                    location.pathname.includes('/workorders')
                  )}
                {features &&
                  features.includes('teams') &&
                  listItem(
                    'nav-teams',
                    '/teams',
                    <TeamIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/teams')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.teams'),
                    location.pathname.includes('/teams')
                  )}
                {features &&
                  groups &&
                  features.includes('companyAdmin') &&
                  groups.includes('companyAdmin') &&
                  listItem(
                    'nav-company-admin',
                    '/company-admin',
                    <BusinessIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/company-admin')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.company-admin'),
                    location.pathname.includes('/company-admin')
                  )}
                {features &&
                  groups &&
                  features.includes('reports') &&
                  (groups.includes('companyAdmin') || groups.includes('admin') || groups.includes('teamLeader')) &&
                  listItem(
                    'nav-statistics',
                    '/statistics',
                    <StatisticsIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/statistics')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.statistics'),
                    location.pathname.includes('/statistics')
                  )}
                {groups &&
                  (groups.includes('admin') || groups.includes('inputDataManager')) &&
                  listItem(
                    'nav-admin',
                    '/admin',
                    <SettingsIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/admin')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.admin'),
                    location.pathname.includes('/admin')
                  )}
                {features &&
                  groups &&
                  features.includes('networks') &&
                  networkAccess &&
                  (groups.includes('admin') || groups.includes('sales')) &&
                  listItem(
                    'nav-networks',
                    '/networks',
                    <AccountTreeIcon
                      sx={{
                        backgroundColor:
                          location.pathname.includes('/networks') && !location.pathname.includes('/benefits')
                            ? theme.palette.secondary.main
                            : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.networks'),
                    location.pathname.includes('/networks') && !location.pathname.includes('/benefits')
                  )}
                {features &&
                  features.includes('scout') &&
                  person &&
                  listItem(
                    'nav-scout',
                    '/scout',
                    <ListAltRoundedIcon
                      sx={{
                        backgroundColor: location.pathname === '/scout' ? theme.palette.secondary.main : 'inherit',
                        borderRadius: '3px',
                        transform: 'rotate(-180deg)',
                      }}
                    />,
                    t('navigation.scout'),
                    location.pathname === '/scout'
                  )}
                {features &&
                  groups &&
                  features.includes('salespool') &&
                  groups.includes('sales') &&
                  listItem(
                    'nav-salespool',
                    '/salespool',
                    <ShoppingBasketIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/salespool')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.salespool'),
                    location.pathname.includes('/salespool')
                  )}
                {features &&
                  features.includes('chat') &&
                  listItem(
                    'nav-chat',
                    '/chat',
                    <ChatIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/chat') ? theme.palette.secondary.main : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.chat'),
                    location.pathname.includes('/chat')
                  )}
                {networks &&
                  networks?.length > 0 &&
                  listItem(
                    'nav-benefits',
                    '/networks/benefits',
                    <DiamondIcon
                      sx={{
                        backgroundColor: location.pathname.includes('/benefits')
                          ? theme.palette.secondary.main
                          : 'inherit',
                        borderRadius: '3px',
                      }}
                    />,
                    t('navigation.benefits'),
                    location.pathname.includes('/benefits')
                  )}
                {listItem(
                  'nav-help',
                  '/help',
                  <HelpIcon
                    sx={{
                      backgroundColor: location.pathname.includes('/help') ? theme.palette.secondary.main : 'inherit',
                      borderRadius: '3px',
                    }}
                  />,
                  t('navigation.help'),
                  location.pathname.includes('/help')
                )}
                {listItem(
                  'nav-releases',
                  '/releases',
                  <LogoDevIcon
                    sx={{
                      backgroundColor: location.pathname.includes('/releases')
                        ? theme.palette.secondary.main
                        : 'inherit',
                      borderRadius: '3px',
                    }}
                  />,
                  t('navigation.releases'),
                  location.pathname.includes('/releases')
                )}
              </List>
            </nav>
          </Drawer>
        </>
      )}
    </>
  )
}

export default NavigationMenu
